import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiClient } from '../../../api/client';
import { url as apiUrl } from '../../../api';
import { getAuthToken } from '../../../utils/authTokens';
import { coffeeShopPaths } from '../../coffee_card_web/api/paths';

const url = apiUrl || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// types
export interface CoffeeTypeStats {
    count: number;
    averageRating: number;
    lastRated: string;
}

export interface CoffeeShop {
    _id: string;
    name: string;
    place_id?: string;
    shopType?: string;
    coordinates: {
        lat: number;
        lng: number;
    };
    address: string;
    city: string;
    country: string;
    totalRatings: number;
    averageRating: number;
    coffeeTypeStats: { [coffeeType: string]: CoffeeTypeStats };
    googleRating?: number;
    googleReviewCount?: number;
    priceLevel?: number;
    types: string[];
    chainId?: string | null;
    chainName?: string | null;
}

/** item returned by GET /api/coffee/v2/shops/recommend */
export interface RecommendShop {
    _id: string;
    name: string;
    coordinates: { lat: number; lng: number };
    address: string;
    city: string;
    country: string;
    totalRatings: number;
    averageRating: number;
    distanceKm: number;
    adjustedGoogleRating: number;
    googleReviewCount?: number | null;
    googleRating?: number | null;
    score: number;
    coffeeTypeStats: { [k: string]: CoffeeTypeStats };
    topCoffeeTypes: { type: string; averageRating: number; count: number }[];
}

export interface RecommendResult {
    success: boolean;
    clusterExists: boolean;
    recommendations: RecommendShop[];
    unratedGems: RecommendShop[];
    radiusKm: number;
}

export interface CoffeeTypeRating {
    ratings: {
        _id: string;
        overallRating: number;
        rateColor: string;
        description?: string;
        format: any;
        createdAt: string;
    }[];
    totalRatings: number;
    averageRating: number;
    iconName: string;
    displayName: string;
}

export interface CoffeeShopDetail {
    coffeeShop: CoffeeShop;
    coffeeRatings: { [coffeeType: string]: CoffeeTypeRating };
    totalRatingsFound: number;
}

export interface CoffeeType {
    coffeeKey: string;
    displayName: string;
    iconName: string;
    fullName?: string;
}

export type TransportMode = 'foot' | 'bike' | 'car' | 'city';

// hook: fetch all coffee shops
export function useCoffeeShops(options?: { clusterId?: string; shopType?: string; adminLat?: number; adminLng?: number }) {
    const [coffeeShops, setCoffeeShops] = useState<CoffeeShop[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCoffeeShops = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params: Record<string, string> = {};
            if (options?.clusterId) params.clusterId = options.clusterId;
            if (options?.shopType && options.shopType !== 'all') params.shopType = options.shopType;
            if (typeof options?.adminLat === 'number') params.adminLat = String(options.adminLat);
            if (typeof options?.adminLng === 'number') params.adminLng = String(options.adminLng);

            const token = getAuthToken();
            const response = await apiClient.get(`${url}${coffeeShopPaths.list()}`, {
                params,
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (response.data && response.data.coffeeShops) {
                setCoffeeShops(response.data.coffeeShops);
            } else {
                setCoffeeShops([]);
            }
        } catch (err: any) {
            console.error('Error fetching coffee shops:', err);
            if (err.response) {
                const status = err.response.status;
                const message = err.response.data?.error || err.response.statusText;
                if (status === 401) {
                    setError('Authentication failed. Please log in again.');
                } else if (status === 404) {
                    setError('Coffee shops endpoint not found.');
                } else {
                    setError(`Error ${status}: ${message}`);
                }
            } else if (err.request) {
                setError('Network error. Please check your connection.');
            } else {
                setError('An unexpected error occurred.');
            }
        } finally {
            setLoading(false);
        }
    }, [options?.clusterId, options?.shopType, options?.adminLat, options?.adminLng]);

    useEffect(() => {
        fetchCoffeeShops();
    }, [fetchCoffeeShops]);

    return { coffeeShops, loading, error, refetch: fetchCoffeeShops };
}

// hook: fetch coffee shops by cluster
export function useCoffeeShopsByCluster(clusterId: string | null) {
    return useCoffeeShops(clusterId ? { clusterId } : undefined);
}

/**
 * `[neLat, neLng, swLat, swLng]` — matches the backend `?bounds=` ordering.
 */
export type MapBounds = [number, number, number, number];

const MAP_BOUNDS_DEBOUNCE_MS = 300;

function boundsContains(outer: MapBounds, inner: MapBounds): boolean {
    const [oNeLat, oNeLng, oSwLat, oSwLng] = outer;
    const [iNeLat, iNeLng, iSwLat, iSwLng] = inner;
    // simple longitude comparison; anti-meridian-crossing fetches just refetch.
    return iNeLat <= oNeLat && iSwLat >= oSwLat && iNeLng <= oNeLng && iSwLng >= oSwLng;
}

// hook: fetch map data (lightweight)
export function useCoffeeShopsMap(options?: { clusterId?: string; shopType?: string; adminLat?: number; adminLng?: number }) {
    const [coffeeShops, setCoffeeShops] = useState<CoffeeShop[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // last bounds we successfully fetched. If a newer bounds is fully
    // contained inside this, the refetch is a no-op (cache short-circuit).
    const lastFetchedBoundsRef = useRef<MapBounds | null>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reqSeqRef = useRef(0);

    const doFetch = useCallback(async (bounds: MapBounds | null) => {
        const seq = ++reqSeqRef.current;
        try {
            // Only block the UI on global refetches (initial load, filter change,
            // manual refetch). Viewport-scoped `bounds` updates keep the map
            // mounted — toggling loading there tears Leaflet down mid zoom and
            // triggers "_leaflet_pos" errors from zoom transition callbacks.
            if (bounds === null) {
                setLoading(true);
            }
            setError(null);

            const params: Record<string, string> = {};
            if (options?.clusterId) params.clusterId = options.clusterId;
            if (options?.shopType && options.shopType !== 'all') params.shopType = options.shopType;
            if (typeof options?.adminLat === 'number') params.adminLat = String(options.adminLat);
            if (typeof options?.adminLng === 'number') params.adminLng = String(options.adminLng);
            if (bounds) {
                params.bounds = bounds.join(',');
            }

            const token = getAuthToken();
            const response = await apiClient.get(`${url}${coffeeShopPaths.mapData()}`, {
                params,
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (seq !== reqSeqRef.current) return; // stale response

            if (response.data && response.data.coffeeShops) {
                setCoffeeShops(response.data.coffeeShops);
            } else {
                setCoffeeShops([]);
            }
            lastFetchedBoundsRef.current = bounds;
        } catch (err: any) {
            if (seq !== reqSeqRef.current) return;
            console.error('Error fetching map data:', err);
            setError('Failed to load map data.');
        } finally {
            if (seq === reqSeqRef.current) setLoading(false);
        }
    }, [options?.clusterId, options?.shopType, options?.adminLat, options?.adminLng]);

    const fetchMapData = useCallback(() => doFetch(null), [doFetch]);

    // Public API for the map view: tell the hook the current viewport.
    // Debounces by ~300 ms and skips when the new bounds sit fully inside the
    // last fetched bounds (we already have those markers).
    const setBounds = useCallback((bounds: MapBounds) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const cached = lastFetchedBoundsRef.current;
            if (cached && boundsContains(cached, bounds)) {
                return;
            }
            void doFetch(bounds);
        }, MAP_BOUNDS_DEBOUNCE_MS);
    }, [doFetch]);

    useEffect(() => {
        // initial fetch loads the unfiltered "all approved" set so the map
        // has something to render before the first moveend fires.
        void doFetch(null);
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [doFetch]);

    return { coffeeShops, loading, error, refetch: fetchMapData, setBounds };
}

// hook: fetch single coffee shop
export function useCoffeeShop(id: string | undefined) {
    const [data, setData] = useState<CoffeeShopDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchDetail = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            setError(null);

            const token = getAuthToken();
            const response = await apiClient.get(`${url}${coffeeShopPaths.byId(id)}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (response.data.success) {
                setData(response.data);
            } else {
                setError('Failed to load coffee shop details.');
            }
        } catch (err: any) {
            console.error('Error fetching coffee shop:', err);
            if (err.response?.status === 404) {
                setError('Coffee shop not found.');
            } else {
                setError('Failed to load coffee shop details.');
            }
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    return { data, loading, error, refetch: fetchDetail };
}

// hook: fetch cluster-scoped recommend (top picks + unrated gems)
export function useRecommend() {
    const [data, setData] = useState<RecommendResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchRecommend = useCallback(async (
        lat: number,
        lng: number,
        transport: TransportMode = 'foot'
    ) => {
        try {
            setLoading(true);
            setError(null);
            setData(null);

            const token = getAuthToken();
            if (!token) {
                setError('Please sign in to get recommendations.');
                return;
            }

            const response = await apiClient.get<RecommendResult>(`${url}${coffeeShopPaths.recommend()}`, {
                params: { lat, lng, transport },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                setData(response.data);
            }
        } catch (err: any) {
            console.error('Error fetching recommend:', err);
            setError(err.response?.data?.error || 'Failed to get recommendations.');
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        data,
        recommendations: data?.recommendations ?? [],
        unratedGems: data?.unratedGems ?? [],
        clusterExists: data?.clusterExists ?? false,
        radiusKm: data?.radiusKm ?? 0,
        loading,
        error,
        fetchRecommend
    };
}

// hook: fetch recommendations (coffee-type based, existing)
export function useShopRecommendations() {
    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchRecommendations = useCallback(async (
        lat: number,
        lng: number,
        coffeeType: string,
        transport: TransportMode = 'foot'
    ) => {
        try {
            setLoading(true);
            setError(null);
            setRecommendations([]);

            const token = getAuthToken();
            if (!token) {
                setError('Please sign in to get recommendations.');
                return;
            }

            const response = await apiClient.get(`${url}${coffeeShopPaths.recommendations()}`, {
                params: { lat, lng, coffeeType, transport },
                headers: { Authorization: `Bearer ${token}` }
            });

            setRecommendations(response.data?.recommendations || []);
        } catch (err: any) {
            console.error('Error fetching recommendations:', err);
            setError('Failed to get recommendations.');
        } finally {
            setLoading(false);
        }
    }, []);

    return { recommendations, loading, error, fetchRecommendations };
}

// hook: fetch coffee types
export function useCoffeeTypes() {
    const [coffeeTypes, setCoffeeTypes] = useState<CoffeeType[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTypes = async () => {
            try {
                const token = getAuthToken();
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const response = await apiClient.get(`${url}/api/coffee/types`, { headers });
                if (response.data?.success) {
                    setCoffeeTypes(response.data.data);
                }
            } catch (e) {
                // silent fail
            } finally {
                setLoading(false);
            }
        };
        fetchTypes();
    }, []);

    return { coffeeTypes, loading };
}

// hook: fetch coffees by ids (for detail page)
export function useCoffeesByIds(ids: string[]) {
    const [coffees, setCoffees] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(false);

    // stable key for dependency
    const idsKey = useMemo(() => ids.join(','), [ids]);

    useEffect(() => {
        if (ids.length === 0) return;

        const fetchCoffees = async () => {
            try {
                setLoading(true);
                const token = getAuthToken();
                const response = await apiClient.post(
                    `${url}/api/coffee-rate/coffees/by-ids`,
                    { ids },
                    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                );
                if (response.data?.success) {
                    const map: Record<string, any> = {};
                    (response.data.data || []).forEach((c: any) => {
                        map[c._id] = c;
                    });
                    setCoffees(map);
                }
            } catch (e) {
                // silent fail
            } finally {
                setLoading(false);
            }
        };

        fetchCoffees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    return { coffees, loading };
}

// Bayesian-adjusted Google rating (1–5 scale) for display when review count is low
export function adjustedGoogleRating(googleRating: number | undefined, reviewCount: number | undefined): number {
    const n = reviewCount ?? 0;
    const r = googleRating ?? 0;
    if (n === 0 && r === 0) return 0;
    return (r * n + 1 + 5) / (n + 2);
}

// utility: get icon url
export function getCoffeeIconUrl(iconName: string): string {
    return `${url}/api/icons/icon/${iconName}`;
}

// utility: rating color
export function getRatingColor(rating: number): string {
    if (rating >= 8) return '#2bb941';
    if (rating >= 5) return '#f49f0c';
    return '#e54343';
}

// utility: interpolate rating color (gradient)
export function interpolateRatingColor(rating: number): string {
    const clamp = (v: number) => Math.max(0, Math.min(1, v));
    const factor = clamp(rating / 10);

    const startColor = { r: 229, g: 67, b: 67 };   // #e54343
    const middleColor = { r: 244, g: 159, b: 12 }; // #f49f0c
    const endColor = { r: 43, g: 185, b: 65 };     // #2bb941

    let r, g, b;
    if (factor < 0.5) {
        const t = factor / 0.5;
        r = startColor.r + (middleColor.r - startColor.r) * t;
        g = startColor.g + (middleColor.g - startColor.g) * t;
        b = startColor.b + (middleColor.b - startColor.b) * t;
    } else {
        const t = (factor - 0.5) / 0.5;
        r = middleColor.r + (endColor.r - middleColor.r) * t;
        g = middleColor.g + (endColor.g - middleColor.g) * t;
        b = middleColor.b + (endColor.b - middleColor.b) * t;
    }

    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}
